import { TICK_MS } from "../movement/step.ts";

/**
 * Where in time the page draws a shot (ADR-095 decision 17). The map server judges a burst against inputs the page
 * sent a round trip before the landing's snapshot arrives, so the page lands the shot early by its round trip: what the
 * player sees at the burst, its own character in or out of the circle, is then what the server judged, to within
 * jitter. The lead is held to `PROJECTILE_LEAD_CAP_MS` and to half of what is left of the flight, and reads a smoothed
 * round trip, so one slow pong does not move a shot in the air. Every function here is pure and allocates nothing.
 */

/**
 * The most a shot is landed early, in ms.
 */
export const PROJECTILE_LEAD_CAP_MS = 250;

/**
 * The share of a new round trip sample the smoothed value takes.
 */
export const ROUND_TRIP_SHARE = 0.125;

/**
 * The smoothed round trip after a sample: the sample itself the first time, and then the value moved toward the sample
 * by `ROUND_TRIP_SHARE` of the distance. A sample that is no number or below 0 is left out.
 *
 * @param previous The smoothed value so far, null before any sample.
 * @param sampleMs The round trip just measured, in ms.
 * @returns The smoothed value, null while no sample has counted.
 */
export function smoothRoundTrip(previous: number | null, sampleMs: number): number | null {
  if (!(sampleMs >= 0)) {
    return previous;
  }
  if (previous === null) {
    return sampleMs;
  }
  return previous + (sampleMs - previous) * ROUND_TRIP_SHARE;
}

/**
 * When a shot lands on the page: what is left of its flight after its arrival, less the round trip, the lead held to
 * the cap and to half of what is left. A shot as old as its flight, and a flight of 0, land as they arrive.
 *
 * @param arrivalMs When the launch arrived, on the page's clock.
 * @param flightTicks Ticks from the launch to the landing, as the launch carries them.
 * @param age Ticks since the launch at the frame that carried it.
 * @param rttMs The smoothed round trip in ms, null while the page has measured none, which reads 0.
 * @returns The landing time on the same clock, never before the arrival.
 */
export function projectileLandsAt(arrivalMs: number, flightTicks: number, age: number, rttMs: number | null): number {
  const left = Math.max(0, flightTicks - age) * TICK_MS;
  const roundTrip = rttMs !== null && rttMs > 0 ? rttMs : 0;
  return arrivalMs + left - Math.min(roundTrip, PROJECTILE_LEAD_CAP_MS, left / 2);
}

/**
 * How far along its line a shot is drawn at a time: the fraction its age had covered when it arrived, and then the
 * rest of the line at a constant rate up to its landing time.
 *
 * @param nowMs The time drawn, on the page's clock.
 * @param arrivalMs When the launch arrived.
 * @param landsAtMs When it lands (`projectileLandsAt`).
 * @param flightTicks Ticks from the launch to the landing.
 * @param age Ticks since the launch at the frame that carried it.
 * @returns The fraction of the line covered, from 0 at the origin to 1 at the landing point.
 */
export function projectileProgress(nowMs: number, arrivalMs: number, landsAtMs: number, flightTicks: number, age: number): number {
  if (flightTicks <= 0 || age >= flightTicks || nowMs >= landsAtMs) {
    return 1;
  }
  const from = age / flightTicks;
  if (nowMs <= arrivalMs) {
    return from;
  }
  return from + ((1 - from) * (nowMs - arrivalMs)) / (landsAtMs - arrivalMs);
}

/**
 * The time since a shot's launch at a time drawn, which chooses its strip's frame: the age it arrived with and the
 * time since.
 *
 * @param nowMs The time drawn, on the page's clock.
 * @param arrivalMs When the launch arrived.
 * @param age Ticks since the launch at the frame that carried it.
 * @returns The time since the launch in ms.
 */
export function projectileElapsedMs(nowMs: number, arrivalMs: number, age: number): number {
  return age * TICK_MS + Math.max(0, nowMs - arrivalMs);
}
